import { useEffect, useState } from "react";
import { IParams } from "../types/interfaces";

interface IProps {
  service: (query: IParams) => Promise<any>;
  pagination?: {
    current: number | undefined;
    pageSize: number | undefined;
    sorter?: {
      field: string;
      order: string;
    };
  };
  isOptions?: boolean;
  filters?: Record<any, any>;
}

/**
 * useFetch abstracts native Axios logic to map pagination formats (Page, Limit, Order, Search)
 * unifying Datatable responses natively matching Ent conventions.
 */
const useFetch = ({
  service,
  pagination,
  isOptions = false,
  filters,
}: IProps) => {
  const [data, setData] = useState<any>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    service({
      page: pagination?.current || 1,
      limit: pagination?.pageSize || 10,
      field: pagination?.sorter?.field,
      order: pagination?.sorter?.order,
      ...filters,
    })
      .then((response) => {
        const responseData = response?.data ?? response ?? [];
        const items = Array.isArray(responseData) ? responseData : [];
        isOptions
          ? setData(formatOptions(items))
          : setData(items);
        setTotal(response?.total ?? response?.count ?? 0);
      })
      .catch((error) => setError(error))
      .finally(() => setLoading(false));
  }, [pagination?.current, pagination?.pageSize, filters, pagination?.sorter]);

  const refetch = () => {
    setLoading(true);
    service({
      page: pagination?.current || 1,
      limit: pagination?.pageSize || 10,
    })
      .then((response) => {
        setTotal(response?.total ?? response?.count ?? 0);
        const responseData = response?.data ?? response ?? [];
        const items = Array.isArray(responseData) ? responseData : [];
        isOptions
          ? setData(formatOptions(items))
          : setData(items);
      })
      .catch((error) => setError(error))
      .finally(() => setLoading(false));
  };

  const formatOptions = (data: any) =>
    data.map((item: any) => ({ label: item.name, value: item.id }));

  const startLoading = () => {
    setLoading(true);
  };

  return { data, error, loading, total, refetch, startLoading, setData };
};

export default useFetch;
